import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, imgUrl } from "@/lib/api";
import { Heart, ArrowRight, ShieldCheck, Sun, GraduationCap } from "lucide-react";
import AnthemSection from "@/components/AnthemSection";

const values = [
  { icon: ShieldCheck, title: "Care", desc: "A safe and stable environment, nutritious food, healthcare, education, protection, and people children can trust." },
  { icon: Heart, title: "Love", desc: "Kindness, encouragement, belonging, positive relationships, and knowing that someone genuinely cares." },
  { icon: Sun, title: "Hope", desc: "The opportunity to heal, rebuild confidence, dream about the future, and believe that tomorrow can be different." },
  { icon: GraduationCap, title: "A Chance at Life", desc: "Education, technology, skills, mentorship, opportunity, and the support children need to build brighter futures." },
];

const affirmations = ["I am safe.", "I am loved.", "I matter.", "I belong.", "I can learn.", "I can dream.", "I have a future."];

const openingStory = [
  "Mercy was a dear childhood friend of our founder. Like our founder, Mercy was an orphaned child. Their shared experiences gave them an understanding of what it meant to grow up without the protection, stability, care, and family support every child deserves.",
  "Tragically, Mercy experienced abuse as a child and passed away far too soon. Her loss had a lasting impact on our founder and strengthened her desire to help vulnerable children facing difficult circumstances.",
  "Mercy's story became part of the inspiration behind a larger mission: to help change the experience of vulnerable children by surrounding them with care, love, protection, hope, education, and an opportunity to build a future.",
];

export default function StoryPage() {
  const [story, setStory] = useState(null);
  const [others, setOthers] = useState([]);
  const [donate, setDonate] = useState("");

  useEffect(() => {
    api.get("/stories").then(({ data }) => {
      setStory(data.find((s) => s.is_memorial) || data[0]);
      setOthers(data.filter((s) => !s.is_memorial));
    }).catch(() => {});
    api.get("/settings").then(({ data }) => setDonate(data.gofundme_url)).catch(() => {});
  }, []);

  const donateUrl = donate || "https://gofund.me/15d8987b7";

  return (
    <div data-testid="story-page" className="bg-[#FBF8F0]">
      {/* MEMORIAL — image left, story right (image first on mobile) */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}>
          {story?.image_url && (
            <img src={imgUrl(story.image_url)} alt="A young Ethiopian girl at golden hour in a rural village"
              className="w-full h-auto rounded-3xl shadow-[0_30px_60px_rgba(31,77,46,0.18)]" data-testid="mercy-portrait" />
          )}
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
          <p className="text-xs tracking-[0.28em] uppercase font-bold text-[#7C5CBF] mb-4">In Memory of Mercy</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1F4D2E] leading-[1.05]">Her Story. Our Inspiration. Our Mission.</h1>
          <div className="mt-7 space-y-5">
            {openingStory.map((p, i) => (
              <p key={i} className="text-[#4F6B54] leading-relaxed">{p}</p>
            ))}
          </div>
        </motion.div>
      </section>

      {/* CHANGING THE EXPERIENCE */}
      <section className="bg-white border-y border-[#1F4D2E]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-3xl mb-12">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Changing the experience of childhood</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-[#1F4D2E] leading-snug">We cannot change what Mercy experienced. But we can create something different for the children who come after her.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-[#FBF8F0] border border-[#1F4D2E]/10 rounded-2xl p-7" data-testid={`value-${v.title.toLowerCase().replace(/ /g, '-')}`}>
                <span className="w-12 h-12 rounded-full bg-[#7C5CBF]/12 text-[#7C5CBF] flex items-center justify-center mb-5"><v.icon size={23} /></span>
                <h3 className="font-display text-xl font-semibold text-[#1F4D2E] mb-2 uppercase tracking-wide">{v.title}</h3>
                <p className="text-[#4F6B54] text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE WANT EVERY CHILD TO KNOW */}
      <section className="bg-[#1F4D2E] text-white relative overflow-hidden">
        <span className="absolute -top-16 -left-16 w-72 h-72 rounded-full bg-[#7C5CBF]/20" />
        <span className="absolute -bottom-20 -right-10 w-64 h-64 rounded-full bg-[#E0A526]/10" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 py-20 text-center">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E0A526] mb-8">What we want every child to know</p>
          <div className="space-y-3">
            {affirmations.map((a, i) => (
              <motion.p key={a} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
                className="font-display text-3xl sm:text-4xl font-semibold leading-tight">{a}</motion.p>
            ))}
          </div>
        </div>
      </section>

      {/* MERCY'S LEGACY */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-20 text-center">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#7C5CBF] mb-4">Mercy's Legacy</p>
        <p className="text-lg text-[#4F6B54] leading-relaxed mb-5">Mercy's life will not be remembered only by the tragedy of how it ended. We choose to carry forward what her life inspired.</p>
        <p className="text-lg text-[#4F6B54] leading-relaxed mb-10">Her memory lives through every child who finds safety, every child who receives an education, every child who discovers hope, every family that becomes stronger, and every young person who receives an opportunity to build a future.</p>
        <p className="font-display text-3xl sm:text-4xl font-bold text-[#1F4D2E] leading-tight">This is for Mercy.<br />This is for every child.</p>
        <p className="text-sm tracking-[0.25em] uppercase font-bold text-[#C1272D] mt-8">Care • Love • Hope • A Chance at Life</p>

        <div className="mt-12 flex flex-wrap gap-4 justify-center">
          <a href={donateUrl} target="_blank" rel="noopener noreferrer" data-testid="story-donate-btn"
            className="bg-[#C1272D] text-white rounded-full px-8 py-4 font-semibold tracking-wide hover:bg-[#9E1F24] transition-colors inline-flex items-center gap-2"><Heart size={18} fill="white" /> Honour Mercy's memory</a>
          <Link to="/projects" className="border border-[#1F4D2E] text-[#1F4D2E] rounded-full px-8 py-4 font-semibold hover:bg-[#1F4D2E] hover:text-white transition-colors inline-flex items-center gap-2" data-testid="story-projects-btn">Sponsor a room <ArrowRight size={18} /></Link>
        </div>
      </section>

      <AnthemSection />

      {others.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24" data-testid="more-stories">
          <h2 className="font-display text-3xl font-semibold text-[#1F4D2E] mb-8">More stories</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {others.map((o) => (
              <div key={o.id} className="border border-[#1F4D2E]/10 rounded-2xl overflow-hidden bg-white" data-testid={`story-item-${o.id}`}>
                {o.image_url && <img src={imgUrl(o.image_url)} alt={o.title} loading="lazy" className="w-full h-48 object-cover" />}
                <div className="p-6">
                  <h3 className="font-display text-xl font-semibold text-[#1F4D2E] mb-2">{o.title}</h3>
                  {o.subtitle && <p className="text-sm text-[#C1272D] font-medium mb-2">{o.subtitle}</p>}
                  <p className="text-[#4F6B54] text-sm leading-relaxed whitespace-pre-line">{o.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
